import React, { ReactElement, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { reactular } from '@vendhq/reactular'
import {
  WorkflowsAction,
  WorkflowsOutcome,
  RequireCustomFieldsActionHandler,
  StopOrConfirmActionHandler,
  WorkflowsEntityBadgeOptions,
} from '@vend/business-components'
import { useT } from '@transifex/react'
import { useWorkflowsManager, focusGlobalSearch } from '../../ng-compat/hooks'
import {
  SERIAL_NUMBER_FIELD,
  SERIAL_NUMBERS_FIELD,
} from '../../functions/lineItem'
import { $state } from '../../ng-compat/imports'
import { STATES } from '../../../../lib/routes'
import { RegisterWorkflowsEvent } from './workflow.service'
import { SuggestProductsActionHandler } from './SuggestProductsActionHandler'

const BLACKLISTED_ACTIONS_ON_CONFIRMATION = [
  'add_line_item',
  'suggest_products',
  'remove_line_item',
  'set_line_item_tax',
]

export const WorkflowsActionHandler = (): ReactElement | null => {
  const [action, setAction] = useState<
    | {
        event: RegisterWorkflowsEvent
        action?: WorkflowsAction
        onOutcome?: (outcome: WorkflowsOutcome) => void
      }
    | undefined
  >()

  const t = useT()
  const manager = useWorkflowsManager()
  useEffect(() => {
    manager.onAction = (event, action, onOutcome) => {
      setAction({ event, action, onOutcome })
    }
  }, [manager])

  if (!action || !action.action || !action.onOutcome) {
    return null
  }

  const onOutcome = (outcome: WorkflowsOutcome) => {
    focusGlobalSearch()

    if (action.onOutcome) {
      return action.onOutcome(outcome)
    }
  }

  if (
    BLACKLISTED_ACTIONS_ON_CONFIRMATION.includes(action.action.type) &&
    $state.current.name === STATES.SALE_CONFIRMATION
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      `Action "${action.action.type}" is not supported in sale confirmation`
    )
    action.onOutcome({ proceed: true, modified: false })
    return null
  }

  switch (action.action.type) {
    case 'confirm':
    case 'stop':
      return createPortal(
        <StopOrConfirmActionHandler
          event={action.event}
          action={action.action}
          manager={manager}
          onOutcome={onOutcome}
        />,
        document.body
      )
    case 'require_custom_fields':
    case 'add_custom_fields': {
      const entityBadge: WorkflowsEntityBadgeOptions | undefined =
        action.action.entity === 'line_item'
          ? {
              type: 'product',
              id: manager.findLineItem(action.action.entity_id)?.product.id,
            }
          : undefined

      const customFields =
        action.action.type === 'add_custom_fields'
          ? action.action.add_custom_fields
          : action.action.required_custom_fields

      const isSerialNumberAction = customFields.some(
        cf =>
          cf.field.name === SERIAL_NUMBER_FIELD ||
          cf.field.name === SERIAL_NUMBERS_FIELD
      )

      const lineItem = manager.findLineItem(action.action.entity_id)
      const quantity = parseInt(String(lineItem?.quantity ?? '1'), 10)

      const getFieldErrorMessage = (
        fieldName: string,
        value: { stringValue?: string; stringArrayValue?: string[] } | undefined
      ): string | undefined => {
        if (fieldName === SERIAL_NUMBER_FIELD) {
          if (!value?.stringValue?.trim()) {
            return t('Enter a serial number', {
              _context: 'Serial number popover error message',
            })
          }
          return undefined
        }
        if (fieldName === SERIAL_NUMBERS_FIELD) {
          const entered = value?.stringArrayValue?.length ?? 0
          const remaining = quantity - entered
          if (remaining <= 0) return undefined

          if (entered === 0) {
            return t(
              '{remaining, plural, one {Enter a serial number} other {Enter {remaining} serial numbers}}',
              {
                remaining,
                _context: 'Serial number popover error message',
              }
            )
          }

          return t(
            '{remaining, plural, one {Enter {remaining} more serial number} other {Enter {remaining} more serial numbers}}',
            {
              remaining,
              _context: 'Serial number popover error message',
            }
          )
        }
        return undefined
      }

      return createPortal(
        <RequireCustomFieldsActionHandler
          event={action.event}
          action={action.action}
          manager={manager}
          onOutcome={onOutcome}
          entityBadge={entityBadge}
          getFieldErrorMessage={
            isSerialNumberAction ? getFieldErrorMessage : undefined
          }
        />,
        document.body
      )
    }
    case 'set_custom_field':
      manager
        .handleSetCustomFieldAction(action.event, action.action)
        .then(onOutcome)
      return null
    case 'add_line_item':
      manager
        .handleAddLineItemAction(action.event, action.action)
        .then(onOutcome)
      return null
    case 'remove_line_item':
      manager
        .handleRemoveLineItemAction(action.event, action.action)
        .then(onOutcome)
      return null
    case 'suggest_products':
      return createPortal(
        <SuggestProductsActionHandler
          event={action.event}
          action={action.action}
          manager={manager}
          onOutcome={onOutcome}
        />,
        document.body
      )
    case 'set_customer':
      manager
        .handleSetCustomerAction(action.event, action.action)
        .then(onOutcome)
      return null
    case 'set_line_item_tax':
      manager
        .handleSetLineItemTaxAction(action.event, action.action)
        .then(onOutcome)
      return null
    default:
      // eslint-disable-next-line no-console
      console.warn('Unsupported Workflows action', action.action)
      action.onOutcome({ proceed: true, modified: false })
      return null
  }
}

export const WorkflowsActionHandlerComponent = reactular(
  WorkflowsActionHandler,
  ['event', 'action', 'manager', 'onOutcome'],
  'reactWrapper'
)
